import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import api from '../../api/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import { redirectByRole } from '../../utils/roleRedirect';
import { getStoredAuthUser } from '../../utils/authStorage';

const TwoFactorSetup = () => {
  const navigate = useNavigate();
  const { completeAuthSession } = useAuth();
  const tempToken = useMemo(() => sessionStorage.getItem('twofa_temp_token'), []);

  const [qrDataUrl, setQrDataUrl] = useState('');
  const [otp, setOtp] = useState('');
  const [loadingQr, setLoadingQr] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [ackBackupCodes, setAckBackupCodes] = useState(false);

  const loadQr = useCallback(async ({ regenerate = false } = {}) => {
    try {
      setLoadingQr(true);
      setErrorMessage('');

      if (!tempToken) {
        setErrorMessage('Your session expired. Please login again.');
        return;
      }

      const response = await api.auth.setup2fa(tempToken, { regenerate });
      if (!response.data?.success) {
        const code = response.data?.code;
        if (code === '2FA_ALREADY_CONFIGURED') {
          navigate('/2fa/verify', { replace: true });
          return;
        }
        setErrorMessage(response.data?.message || 'Failed to initialize 2FA setup');
        return;
      }

      const qr = response.data?.data?.qr_data_url;
      if (!qr) {
        setErrorMessage('QR code was not returned by server');
        return;
      }

      setQrDataUrl(qr);
    } catch (error) {
      const code = error.response?.data?.code;
      if (code === '2FA_ALREADY_CONFIGURED') {
        navigate('/2fa/verify', { replace: true });
        return;
      }
      setErrorMessage(error.response?.data?.message || 'Failed to initialize 2FA setup');
    } finally {
      setLoadingQr(false);
    }
  }, [tempToken, navigate]);

  useEffect(() => {
    loadQr();
  }, [loadQr]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!tempToken) {
      setErrorMessage('Your session expired. Please login again.');
      return;
    }

    if (!qrDataUrl) {
      setErrorMessage('QR code is not ready yet. Please wait or go back to login and try again.');
      return;
    }

    const normalizedOtp = String(otp || '').replace(/\s+/g, '').trim();
    if (!normalizedOtp) {
      setErrorMessage('OTP code is required');
      return;
    }

    try {
      setSubmitting(true);
      const response = await api.auth.verify2faSetup({ tempToken, otp: normalizedOtp });

      if (!response.data?.success) {
        setErrorMessage(response.data?.message || 'Invalid OTP');
        return;
      }

      const tokens = response.data?.data?.tokens;
      const user = response.data?.data?.user;
      const codes = response.data?.data?.backup_codes || [];
      if (!tokens?.access_token || !user) {
        setErrorMessage('Invalid response from server');
        return;
      }

      const safeUser = completeAuthSession({
        user,
        tokens,
        message: response.data.message || '2FA enabled successfully'
      });

      if (!safeUser) {
        setErrorMessage('Failed to save login session');
        return;
      }

      if (Array.isArray(codes) && codes.length) {
        setBackupCodes(codes);
        return;
      }

      redirectByRole(safeUser.role, navigate);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Failed to verify OTP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = () => {
    setErrorMessage('');
    if (!ackBackupCodes) {
      setErrorMessage('Please confirm you saved your backup codes.');
      return;
    }

    const parsed = getStoredAuthUser();
    if (parsed?.role !== undefined && parsed?.role !== null) {
      redirectByRole(parsed.role, navigate);
      return;
    }

    navigate('/login', { replace: true });
  };

  const handleBackToLogin = () => {
    sessionStorage.removeItem('twofa_temp_token');
    sessionStorage.removeItem('twofa_user_preview');
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-vh-100 d-flex align-items-center brand-app-login-page">
      <Container>
        <Row className="justify-content-center">
          <Col xs={12} md={7} lg={5} xl={4}>
            <Card className="shadow-lg border-0" style={{ borderRadius: '12px' }}>
              <Card.Body className="p-4">
                <h4 className="fw-bold mb-2">Set up Two-Factor Authentication</h4>
                <p className="text-muted mb-3" style={{ fontSize: 14 }}>
                  Scan this QR code with Google Authenticator or Microsoft Authenticator, then enter the 6-digit code.
                </p>

                {errorMessage ? (
                  <Alert variant="danger">
                    {errorMessage}
                    {tempToken && !backupCodes.length ? (
                      <div className="mt-2">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => loadQr({ regenerate: true })}
                          disabled={loadingQr}
                        >
                          Retry QR setup
                        </Button>
                      </div>
                    ) : null}
                  </Alert>
                ) : null}

                {backupCodes.length ? (
                  <Alert variant="warning">
                    <div className="fw-semibold mb-2">Save your backup codes</div>
                    <div className="mb-2" style={{ fontSize: 13 }}>
                      Store these in a safe place. Each code can be used once if you lose access to your authenticator.
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {backupCodes.map((code) => (
                        <span
                          key={code}
                          className="px-2 py-1 rounded"
                          style={{ backgroundColor: '#fff3cd', border: '1px solid #ffe69c', fontFamily: 'monospace' }}
                        >
                          {code}
                        </span>
                      ))}
                    </div>
                    <Form.Check
                      className="mt-3"
                      type="checkbox"
                      id="ack-backup-codes"
                      label="I saved these backup codes"
                      checked={ackBackupCodes}
                      onChange={(e) => setAckBackupCodes(e.target.checked)}
                    />
                    <Button
                      className="w-100 fw-semibold mt-3 brand-app-login-btn"
                      onClick={handleContinue}
                    >
                      Continue
                    </Button>
                  </Alert>
                ) : null}

                {loadingQr ? (
                  <div className="d-flex align-items-center justify-content-center py-4">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <div className="d-flex justify-content-center mb-3">
                    {qrDataUrl ? (
                      <img
                        src={qrDataUrl}
                        alt="2FA QR Code"
                        style={{ width: 220, height: 220 }}
                      />
                    ) : null}
                  </div>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ fontSize: 14 }}>
                      OTP Code
                    </Form.Label>
                    <Form.Control
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      disabled={loadingQr || submitting || backupCodes.length > 0}
                    />
                  </Form.Group>

                  <Button
                    type="submit"
                    className="w-100 fw-semibold brand-app-login-btn"
                    disabled={loadingQr || submitting || backupCodes.length > 0 || !qrDataUrl}
                  >
                    {submitting ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                        Verifying...
                      </>
                    ) : (
                      'Enable 2FA'
                    )}
                  </Button>
                </Form>

                <Button
                  variant="link"
                  className="w-100 mt-2 text-decoration-none"
                  onClick={handleBackToLogin}
                  disabled={submitting}
                >
                  Back to Login
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default TwoFactorSetup;

